import { useCallback, useEffect, useRef, useState } from 'react';
import {
  addReaction as apiAddReaction,
  deleteReaction as apiDeleteReaction,
  deleteMessage as apiDeleteMessage,
  editMessage as apiEditMessage,
  getMessageReactions as apiGetMessageReactions,
  sendMessage as apiSendMessage,
  uploadMedia as apiUploadMedia,
  getMessageContext,
  getMessages,
} from '../api/messaging.api';
import {
  LOCAL_REACTION_HOLD_MS,
  applyReactionPayload,
  extractReactionPayload,
  parseBooleanish,
  resolveMessageReactionEntries,
  resolveReactionMutationFromResponse,
  resolveReactionPayloadFromMessageResponse,
  resolveReactionType,
  withOptimisticReaction,
  withOptimisticReactionRemoved,
} from '../utils/ReactionUtils';
import { shouldTreatConversationAsEncrypted } from '../utils/e2eeUtils';
import { notifyErrorToast } from 'utils/notifications/toast.utils';

export function useMessages({ e2ee = null, getConversationById = null } = {}) {
  const [messagesByConversation, setMessagesByConversation] = useState({});
  const [loading, setLoading] = useState(false);
  const messagesByConversationRef = useRef(messagesByConversation);

  useEffect(() => {
    messagesByConversationRef.current = messagesByConversation;
  }, [messagesByConversation]);

  const updateMessageById = useCallback((conversationId, messageId, updater) => {
    if (!conversationId || messageId == null || typeof updater !== 'function') return;
    setMessagesByConversation((prev) => {
      const existing = prev[conversationId] || [];
      let didUpdate = false;
      const updated = existing.map((message) => {
        if (`${message?.id}` !== `${messageId}`) return message;
        didUpdate = true;
        const nextMessage = updater(message);
        return nextMessage == null ? message : nextMessage;
      });
      if (!didUpdate) return prev;
      return { ...prev, [conversationId]: updated };
    });
  }, []);

  const mergeServerMessageWithLocalReaction = useCallback((serverMessage, localMessage) => {
    if (!serverMessage || typeof serverMessage !== 'object') return serverMessage;
    if (!localMessage || typeof localMessage !== 'object') return serverMessage;

    const localReactionPayload = extractReactionPayload(localMessage);
    if (!localReactionPayload) return serverMessage;

    const localUpdatedAt = Number(localMessage?.metadata?.__localReactionUpdatedAt);
    if (!Number.isFinite(localUpdatedAt)) return serverMessage;
    if (Date.now() - localUpdatedAt > LOCAL_REACTION_HOLD_MS) return serverMessage;

    const serverReactionPayload = extractReactionPayload(serverMessage);
    if (serverReactionPayload) return serverMessage;

    return applyReactionPayload(serverMessage, localReactionPayload);
  }, []);

  const loadMessages = useCallback(
    async (conversationId, params = {}, conversation = null) => {
      if (!conversationId) return [];
      setLoading(true);
      try {
        const data = await getMessages(conversationId, params);
        // API returns { messages: [...] }
        const messages = Array.isArray(data?.messages) ? data.messages : [];
        const resolvedConversation =
          conversation ||
          (typeof getConversationById === 'function' ? getConversationById(conversationId) : null);

        const hydratedMessages =
          e2ee?.isEnabled && shouldTreatConversationAsEncrypted(resolvedConversation, messages)
            ? await e2ee.decryptMessagesForConversation(
                conversationId,
                messages,
                resolvedConversation,
              )
            : messages;

        setMessagesByConversation((prev) => {
          const existing = prev[conversationId] || [];
          const existingById = new Map(existing.map((message) => [`${message?.id}`, message]));
          const nextMessages = hydratedMessages.map((message) =>
            mergeServerMessageWithLocalReaction(message, existingById.get(`${message?.id}`)),
          );

          if (params.after) {
            // Load newer messages - deduplicate and append
            const existingIds = new Set(existing.map((m) => m.id));
            const newMessages = nextMessages.filter((m) => !existingIds.has(m.id));
            return { ...prev, [conversationId]: [...existing, ...newMessages] };
          }

          if (params.before) {
            // Load older messages - deduplicate and prepend
            const existingIds = new Set(existing.map((m) => m.id));
            const newMessages = nextMessages.filter((m) => !existingIds.has(m.id));
            return { ...prev, [conversationId]: [...newMessages, ...existing] };
          }

          // Initial load - replace all
          return { ...prev, [conversationId]: nextMessages };
        });

        return hydratedMessages;
      } catch (err) {
        console.error(`[useMessages] loadMessages failed for ${conversationId}:`, err);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [e2ee, getConversationById, mergeServerMessageWithLocalReaction],
  );

  const loadMessageContext = useCallback(
    async (conversationId, messageId, params = {}, conversation = null) => {
      if (!conversationId || !messageId) return null;

      try {
        const data = await getMessageContext(conversationId, messageId, params);

        // API returns { message: "...", data: { targetMessage, messagesBefore, messagesAfter, totalBefore, totalAfter } }
        // Extract the actual data from the nested structure
        const contextData = data?.data || data;
        const {
          targetMessage,
          messagesBefore = [],
          messagesAfter = [],
          totalBefore,
          totalAfter,
        } = contextData;

        if (!targetMessage) return null;

        const resolvedConversation =
          conversation ||
          (typeof getConversationById === 'function' ? getConversationById(conversationId) : null);
        const mergedContextMessages = [...messagesBefore, targetMessage, ...messagesAfter];
        const contextMessages =
          e2ee?.isEnabled &&
          shouldTreatConversationAsEncrypted(resolvedConversation, mergedContextMessages)
            ? await e2ee.decryptMessagesForConversation(
                conversationId,
                mergedContextMessages,
                resolvedConversation,
              )
            : mergedContextMessages;

        setMessagesByConversation((prev) => {
          const existing = prev[conversationId] || [];
          const existingIds = new Set(existing.map((m) => m.id));

          // Filter out duplicates and merge
          const newMessages = contextMessages.filter((m) => !existingIds.has(m.id));
          const merged = [...existing, ...newMessages];

          // Sort by sent_at timestamp to maintain chronological order
          merged.sort((a, b) => new Date(a.sent_at) - new Date(b.sent_at));

          return { ...prev, [conversationId]: merged };
        });

        return { targetMessage, totalBefore, totalAfter };
      } catch (err) {
        console.error(
          `[useMessages] loadMessageContext failed for ${conversationId}/${messageId}:`,
          err,
        );
        return null;
      }
    },
    [e2ee, getConversationById],
  );

  const sendMessage = useCallback(
    async (
      conversationId,
      content,
      currentUserId,
      replyToMessageId = null,
      conversation = null,
    ) => {
      if (!conversationId || !content?.trim()) return null;

      const optimisticId = `temp-${Date.now()}`;
      const optimisticMsg = {
        id: optimisticId,
        conversation_id: conversationId,
        sender_id: currentUserId,
        content: content.trim(),
        content_type: 'text',
        sent_at: new Date().toISOString(),
        edited_at: null,
        deleted_at: null,
        reply_to_message_id: replyToMessageId,
        reactions: [],
        metadata: {},
        _optimistic: true,
      };

      setMessagesByConversation((prev) => ({
        ...prev,
        [conversationId]: [...(prev[conversationId] || []), optimisticMsg],
      }));

      try {
        const resolvedConversation =
          conversation ||
          (typeof getConversationById === 'function' ? getConversationById(conversationId) : null);
        const shouldEncrypt =
          e2ee?.isEnabled &&
          shouldTreatConversationAsEncrypted(resolvedConversation, [optimisticMsg]);

        let payload = {
          content: content.trim(),
          contentType: 'text',
        };

        if (shouldEncrypt) {
          let encryptedPayload = null;
          try {
            encryptedPayload = await e2ee.buildEncryptedSendPayload(
              conversationId,
              content.trim(),
              {
                replyToMessageId,
              },
            );
          } catch (error) {
            const missingConversationKey =
              `${error?.message || ''}`.toLowerCase().includes('conversation key not available') ||
              error?.response?.status === 404;

            if (missingConversationKey && resolvedConversation) {
              await e2ee.createAndShareConversationKey(resolvedConversation);
              encryptedPayload = await e2ee.buildEncryptedSendPayload(
                conversationId,
                content.trim(),
                {
                  replyToMessageId,
                },
              );
            } else if (missingConversationKey) {
              notifyErrorToast(
                'Message',
                { isAddOperation: true },
                {
                  toastId: `error:message:missing-encryption-key:${conversationId}`,
                  errorPayload: {
                    message:
                      'Encryption key is not available for this conversation yet. Please wait for key sync and try again.',
                  },
                },
              );
              throw error;
            }
            throw error;
          }

          payload = {
            ...encryptedPayload,
            contentType: 'text',
            metadata: {},
          };
          if (replyToMessageId) {
            payload.replyToMessageId = replyToMessageId;
          }
        } else if (replyToMessageId) {
          payload.replyToMessageId = replyToMessageId;
        }

        const result = await apiSendMessage(conversationId, payload);
        // API returns { message: "Message sent", data: {...messageObject} }
        const serverMsg = result?.data ?? result;
        const displayServerMsg =
          shouldEncrypt && e2ee?.decryptMessage
            ? await e2ee.decryptMessage(conversationId, serverMsg)
            : serverMsg;

        setMessagesByConversation((prev) => ({
          ...prev,
          [conversationId]: (prev[conversationId] || []).map((m) =>
            m.id === optimisticId ? { ...displayServerMsg, _optimistic: false } : m,
          ),
        }));

        return displayServerMsg;
      } catch (err) {
        console.error('[useMessages] sendMessage failed:', err);
        setMessagesByConversation((prev) => ({
          ...prev,
          [conversationId]: (prev[conversationId] || []).map((m) =>
            m.id === optimisticId ? { ...m, _failed: true } : m,
          ),
        }));
        return null;
      }
    },
    [e2ee, getConversationById],
  );

  const uploadMedia = useCallback(async (conversationId, file, caption, currentUserId) => {
    if (!conversationId || !file) return null;

    const optimisticId = `temp-media-${Date.now()}`;
    const previewUrl = URL.createObjectURL(file);
    const contentType = file.type.startsWith('video/') ? 'video' : 'image';

    const optimisticMsg = {
      id: optimisticId,
      conversation_id: conversationId,
      sender_id: currentUserId,
      content: caption || '',
      content_type: contentType,
      sent_at: new Date().toISOString(),
      edited_at: null,
      deleted_at: null,
      reply_to_message_id: null,
      reactions: [],
      metadata: {
        file: {
          filename: file.name,
          originalname: file.name,
          mimetype: file.type,
          size: file.size,
          url: previewUrl,
        },
      },
      _optimistic: true,
      _uploading: true,
    };

    setMessagesByConversation((prev) => ({
      ...prev,
      [conversationId]: [...(prev[conversationId] || []), optimisticMsg],
    }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (caption) formData.append('caption', caption);

      const result = await apiUploadMedia(conversationId, formData);
      // API returns { message, data: {...messageObject}, processing }
      const serverMsg = result?.data ?? result;

      URL.revokeObjectURL(previewUrl);

      setMessagesByConversation((prev) => ({
        ...prev,
        [conversationId]: (prev[conversationId] || []).map((m) =>
          m.id === optimisticId ? { ...serverMsg, _optimistic: false, _uploading: false } : m,
        ),
      }));

      return serverMsg;
    } catch (err) {
      console.error('[useMessages] uploadMedia failed:', err);
      setMessagesByConversation((prev) => ({
        ...prev,
        [conversationId]: (prev[conversationId] || []).map((m) =>
          m.id === optimisticId ? { ...m, _uploading: false, _failed: true } : m,
        ),
      }));
      return null;
    }
  }, []);

  const deleteMessage = useCallback(async (conversationId, messageId) => {
    try {
      await apiDeleteMessage(conversationId, messageId);
      setMessagesByConversation((prev) => ({
        ...prev,
        [conversationId]: (prev[conversationId] || []).filter((m) => `${m.id}` !== `${messageId}`),
      }));
      return true;
    } catch (err) {
      console.error('[useMessages] deleteMessage failed:', err);
      return false;
    }
  }, []);

  const editMessage = useCallback(async (conversationId, messageId, content) => {
    try {
      const result = await apiEditMessage(conversationId, messageId, { content });
      const updated = result?.message ?? result;
      setMessagesByConversation((prev) => ({
        ...prev,
        [conversationId]: (prev[conversationId] || []).map((m) =>
          `${m.id}` === `${messageId}` ? { ...m, ...updated } : m,
        ),
      }));
    } catch (err) {
      console.error('[useMessages] editMessage failed:', err);
    }
  }, []);

  const syncMessageReactions = useCallback(
    async (conversationId, messageId) => {
      if (!conversationId || messageId == null) return null;
      try {
        const result = await apiGetMessageReactions(conversationId, messageId);
        const reactionPayload = resolveReactionPayloadFromMessageResponse(result);
        if (reactionPayload) {
          updateMessageById(conversationId, messageId, (message) =>
            applyReactionPayload(message, reactionPayload),
          );
        }
        return reactionPayload;
      } catch (err) {
        console.error(
          `[useMessages] syncMessageReactions failed for ${conversationId}/${messageId}:`,
          err,
        );
        return null;
      }
    },
    [updateMessageById],
  );

  const toggleMessageReaction = useCallback(
    async (conversationId, messageId, reactionType, currentUserId) => {
      if (!conversationId || messageId == null) return null;
      const resolvedReactionType = resolveReactionType(reactionType);
      if (!resolvedReactionType) return null;

      const currentMessage = (messagesByConversationRef.current?.[conversationId] || []).find(
        (message) => `${message?.id}` === `${messageId}`,
      );
      if (!currentMessage) return null;

      const currentEntries = resolveMessageReactionEntries(currentMessage, {
        currentUserId,
        parseOwnFlag: parseBooleanish,
      });
      const ownReactionEntry =
        currentEntries.find((entry) => entry?.reactedByCurrentUser === true) || null;
      const shouldRemove = ownReactionEntry?.type === resolvedReactionType;
      const rollbackReactionPayload = extractReactionPayload(currentMessage);

      const optimisticMessage = shouldRemove
        ? withOptimisticReactionRemoved(currentMessage, {
            reactionType: resolvedReactionType,
            reactorUserId: currentUserId,
            currentUserId,
            parseOwnFlag: parseBooleanish,
          })
        : withOptimisticReaction(currentMessage, {
            reactionType: resolvedReactionType,
            reactorUserId: currentUserId,
            currentUserId,
            parseOwnFlag: parseBooleanish,
          });

      updateMessageById(conversationId, messageId, () => optimisticMessage);

      try {
        const result = shouldRemove
          ? await apiDeleteReaction(conversationId, messageId, {
              reactionType: resolvedReactionType,
            })
          : await apiAddReaction(conversationId, messageId, { reactionType: resolvedReactionType });

        const mutationReactionPayload = resolveReactionPayloadFromMessageResponse(result);
        if (mutationReactionPayload) {
          updateMessageById(conversationId, messageId, (message) =>
            applyReactionPayload(message, mutationReactionPayload),
          );
          return mutationReactionPayload;
        }

        const syncedPayload = await syncMessageReactions(conversationId, messageId);
        if (syncedPayload) return syncedPayload;

        const mutationKind =
          resolveReactionMutationFromResponse(result) || (shouldRemove ? 'removed' : 'added');
        return { mutation: mutationKind };
      } catch (err) {
        console.error('[useMessages] toggleMessageReaction failed:', err);
        updateMessageById(conversationId, messageId, (message) => {
          if (!rollbackReactionPayload) return currentMessage;
          return applyReactionPayload(message, rollbackReactionPayload);
        });
        return null;
      }
    },
    [syncMessageReactions, updateMessageById],
  );

  const addMessageOptimistic = useCallback((conversationId, message) => {
    setMessagesByConversation((prev) => {
      const existing = prev[conversationId] || [];
      if (existing.find((m) => `${m.id}` === `${message.id}`)) return prev;
      return { ...prev, [conversationId]: [...existing, message] };
    });
  }, []);

  const updateMessageOptimistic = useCallback((conversationId, messageId, updates) => {
    setMessagesByConversation((prev) => ({
      ...prev,
      [conversationId]: (prev[conversationId] || []).map((m) =>
        `${m.id}` === `${messageId}` ? { ...m, ...updates } : m,
      ),
    }));
  }, []);

  const removeMessageOptimistic = useCallback((conversationId, messageId) => {
    setMessagesByConversation((prev) => ({
      ...prev,
      [conversationId]: (prev[conversationId] || []).filter((m) => `${m.id}` !== `${messageId}`),
    }));
  }, []);

  return {
    messagesByConversation,
    loading,
    loadMessages,
    loadMessageContext,
    sendMessage,
    uploadMedia,
    deleteMessage,
    editMessage,
    syncMessageReactions,
    toggleMessageReaction,
    addMessageOptimistic,
    updateMessageOptimistic,
    removeMessageOptimistic,
  };
}
